import { FormattedMessage } from 'react-intl';
import SearchIcon from '@mui/icons-material/Search';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import { Stack, Divider } from '@mui/material';

import { ModalDrawer } from '@ocx/ui-modals';
import { Button, Typography, SizingMultiplier } from '@ocx/ui';
import { LocationIcon } from '@ocx/ui-icons';

import { LocationSelectorItem } from './location-selector-item.component';
import { locationSelectorModalMessages } from './location-selector-modal.messages';
import { LocalOffersLocationState } from '../local-offers.types';
import { LocationSelectorState } from './location-selector-modal.types';

export type LocationSelectorModalProps = {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  locationState: LocalOffersLocationState;
  customLocationName?: string;
  onSelectLocation: (newLocationState: LocationSelectorState, newCustomLocation?: string) => void;
  onSearchForLocationClick: () => void;
};

export const LocationSelectorModal = ({
  open,
  onClose,
  onExited,
  locationState,
  customLocationName,
  onSelectLocation,
  onSearchForLocationClick,
}: LocationSelectorModalProps) => {
  const isCurrentLocationSelected = locationState === 'nearby';
  const isCustomLocationSelected = locationState === 'custom';
  const isAllLocationsSelected = locationState === 'all';

  return (
    <ModalDrawer open={open} onClose={onClose} onExited={onExited}>
      <Stack p={SizingMultiplier.lg}>
        <Typography variant="h5" component="h1">
          <FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:title']} />
        </Typography>
        <Typography>
          <FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:subtitle']} />
        </Typography>

        <Stack divider={<Divider />} spacing={0} mt={SizingMultiplier.sm}>
          <LocationSelectorItem
            icon={<LocationIcon />}
            title={<FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:useCurrentLocation']} />}
            isSelected={isCurrentLocationSelected}
            onClick={() => onSelectLocation('nearby')}
          />

          <LocationSelectorItem
            icon={<SearchIcon />}
            title={
              isCustomLocationSelected && customLocationName ? (
                customLocationName
              ) : (
                <FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:searchForLocation']} />
              )
            }
            subtitle={
              isCustomLocationSelected ? (
                <FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:searchAnotherLocation']} />
              ) : (
                <FormattedMessage
                  {...locationSelectorModalMessages['locationSelectorModal:findOffersNearSpecificLocation']}
                />
              )
            }
            isSelected={isCustomLocationSelected}
            onClick={onSearchForLocationClick}
          />

          <LocationSelectorItem
            icon={<LibraryBooksOutlinedIcon />}
            title={<FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:showAllOffers']} />}
            subtitle={
              <FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:offersAvailableEverywhere']} />
            }
            isSelected={isAllLocationsSelected}
            onClick={() => onSelectLocation('all')}
          />
        </Stack>

        <Button variant="text" onClick={onClose} fullWidth sx={{ mt: 2 }}>
          <FormattedMessage {...locationSelectorModalMessages['locationSelectorModal:cancel']} />
        </Button>
      </Stack>
    </ModalDrawer>
  );
};
